import { Icon } from '@iconify/react';
import type {
  PadelMaxSets,
  PadelScoringKind,
  PadelTargetScore,
  TambahPadelGameFormState,
} from './padelGenerateUtils';
import PadelCompactStepper from './PadelCompactStepper';

interface TambahPadelGameScoringFieldsProps {
  form: TambahPadelGameFormState;
  onChange: (patch: Partial<TambahPadelGameFormState>) => void;
}

const SCORING: {
  value: PadelScoringKind;
  icon: string;
  title: string;
  hint: string;
}[] = [
  {
    value: 'americano',
    icon: 'mdi:target',
    title: 'Rally Point',
    hint: 'Satu reli, satu poin sampai batas poin',
  },
  {
    value: 'best_of',
    icon: 'mdi:trophy-outline',
    title: 'Scoring Tenis',
    hint: 'Poin 15, 30, 40 dan game per set',
  },
];

export default function TambahPadelGameScoringFields({
  form,
  onChange,
}: TambahPadelGameScoringFieldsProps) {
  return (
    <div className="space-y-3">
      {/* Card 1: Jumlah Lapangan */}
      <div className="flex items-center justify-between rounded-3xl border border-gray-100 bg-white p-4 sm:p-5 shadow-2xs">
        <span className="text-base font-extrabold text-[#0E1726]">Jumlah Lapangan</span>
        <PadelCompactStepper
          label="Jumlah lapangan"
          value={form.numCourts}
          min={1}
          max={12}
          onChange={(numCourts) => onChange({ numCourts })}
        />
      </div>

      {/* Card 2: Tipe Scoring */}
      <div className="rounded-3xl border border-gray-100 bg-white p-4 sm:p-5 shadow-2xs">
        <h2 className="text-base font-extrabold text-[#0E1726]">Tipe Scoring</h2>
        <div className="mt-3.5 space-y-2.5">
          {SCORING.map((opt) => {
            const selected = form.scoringKind === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                aria-label={opt.title}
                aria-pressed={selected}
                onClick={() => onChange({ scoringKind: opt.value })}
                className={`w-full rounded-2xl p-3.5 text-left flex items-center justify-between gap-3.5 transition-all cursor-pointer ${
                  selected
                    ? 'border-2 border-[#FC480D] bg-[#FFF8F5]'
                    : 'border border-gray-100 bg-white hover:border-gray-200'
                }`}
              >
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-colors ${
                    selected
                      ? 'bg-white border border-orange-100 text-[#FC480D]'
                      : 'bg-gray-50 text-gray-400'
                  }`}
                >
                  <Icon icon={opt.icon} className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-extrabold text-[#0E1726]">{opt.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{opt.hint}</p>
                </div>
                {selected ? (
                  <Icon icon="mdi:check-circle" className="h-5 w-5 text-[#FC480D] shrink-0" />
                ) : (
                  <Icon icon="mdi:check-circle" className="h-5 w-5 text-gray-200 shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {form.scoringKind === 'americano' && (
          <div className="mt-4 pt-1">
            <h3 className="text-sm font-extrabold text-[#0E1726]">Batas Poin per Game</h3>
            <div className="mt-2.5 grid grid-cols-3 gap-2.5">
              {([16, 21, 24] as PadelTargetScore[]).map((value) => {
                const selected = form.targetScore === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-label={`${value} poin`}
                    aria-pressed={selected}
                    onClick={() => onChange({ targetScore: value })}
                    className={`h-11 rounded-2xl text-sm font-extrabold transition-all cursor-pointer flex items-center justify-center ${
                      selected
                        ? 'border-2 border-[#FC480D] bg-[#FFF8F5] text-[#FC480D]'
                        : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {value} poin
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {form.scoringKind === 'best_of' && (
          <div className="mt-4 pt-1">
            <h3 className="text-sm font-extrabold text-[#0E1726]">Jumlah Set (Best Of)</h3>
            <div className="mt-2.5 grid grid-cols-2 gap-2.5">
              {([1, 3] as PadelMaxSets[]).map((value) => {
                const selected = form.maxSets === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-label={value === 1 ? '1 Set' : 'Best of 3'}
                    aria-pressed={selected}
                    onClick={() => onChange({ maxSets: value })}
                    className={`h-11 rounded-2xl text-sm font-extrabold transition-all cursor-pointer flex items-center justify-center ${
                      selected
                        ? 'border-2 border-[#FC480D] bg-[#FFF8F5] text-[#FC480D]'
                        : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {value === 1 ? '1 Set' : 'Best of 3'}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-400">
              {form.maxSets === 1
                ? '1 set penentu (race to 6 games, tiebreak di 6-6).'
                : 'Pemenang ditentukan dari tim yang pertama memenangkan 2 set.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
